import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import {
  useGetCitiesQuery,
  useGetCitiesFromGeomancerQuery,
} from '@/pages/Business/pages/Shippings/services/Addresses';
import { Option } from '@/components/inputs/AutocompleteInput';
import { PaginatedRequest } from '@/types';

interface UseCitiesOptionsProps {
  search?: string;
  stateOrDepartmentId?: string;
  departmentName?: string;
  searchParamKey?: 'search' | 'name';
}

const useCitiesOptions = ({
  search = '',
  stateOrDepartmentId,
  departmentName,
  searchParamKey = 'search',
}: UseCitiesOptionsProps) => {
  const {
    debouncedSearch: searchTrimmed,
    shouldSearch: hasMinSearch,
    isDebouncePending,
  } = useAutocompleteSearch(search, 3);
  // Cuando tenemos el departamento (nombre + UUID local) usamos la lista
  // completa de Geomancer, hidratada a UUID local. En cualquier otro caso
  // (p. ej. buscadores que solo filtran por texto) mantenemos el
  // comportamiento anterior contra la tabla local para no romperlos.
  const useGeomancer = Boolean(departmentName && stateOrDepartmentId);

  const paginated: PaginatedRequest = {
    page: 1,
    pageSize: useGeomancer ? 50 : 1000,
  };

  const geomancerQuery = useGetCitiesFromGeomancerQuery(
    {
      paginated,
      department: departmentName ?? '',
      stateOrDepartmentId: stateOrDepartmentId ?? '',
      search: searchTrimmed,
    },
    { skip: !hasMinSearch || !useGeomancer }
  );

  const localQuery = useGetCitiesQuery(
    {
      paginated,
      department: departmentName,
      stateOrDepartmentId,
      search: searchTrimmed,
      searchParamKey,
    },
    { skip: !hasMinSearch || useGeomancer }
  );

  const { data, isLoading, isFetching } = useGeomancer
    ? geomancerQuery
    : localQuery;

  const resolvedCityOptions: Option[] = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((city) => ({
      label: city.name,
      data: city.id,
    }));
  }, [data?.items]);

  const cityOptions = useStableOptions(resolvedCityOptions, {
    isPending: isFetching || isDebouncePending,
    enabled: hasMinSearch || isDebouncePending,
  });

  return {
    cityOptions,
    isLoading: hasMinSearch && (isLoading || isFetching),
  };
};

export default useCitiesOptions;
